/**
 * Фасад софтфона: единственное, что видит приложение.
 *
 * Собирает вместе транспорт, регистрацию, звонок, устройства и ICE-сторож;
 * наружу отдаёт снимок состояния и подписку. React здесь не импортируется —
 * клиент создаётся один раз на вкладку и переживает ремаунт дерева (спека §7.9).
 */
import { Inviter, Registerer, RegistererState as SipRegistererState, RequestPendingError, SessionState, TransportState as SipTransportState, UserAgent, Web, } from 'sip.js';
import { AudioDeviceManager } from './audio-devices.js';
import { asMediaError, isMissingDeviceError } from './media-errors.js';
import { ICE_GATHERING_TIMEOUT_MS, ICE_GRACE_MS, ICE_MAX_RESTARTS, IceWatchdog } from './ice-watchdog.js';
import { ASSIGNMENT_HEADER, CAROUSEL_AUTO_HEADER, CAROUSEL_PHONE_HEADER, OutboundCall, } from './outbound-call.js';
import { systemScheduler } from './ports.js';
import { REGISTER_EXPIRES_SEC, RegistererSupervisor } from './registerer-supervisor.js';
import { SoftphoneStore } from './state.js';
import { TransportSupervisor } from './transport-supervisor.js';
class Softphone {
    #store = new SoftphoneStore();
    #deps;
    #scheduler;
    #audio;
    #stack = null;
    #transport = null;
    #registerer = null;
    #call = null;
    #watchdog = null;
    #unbindIncoming = null;
    /**
     * Звонок УЖЕ создаётся, но в сторе его ещё нет: исходящий попадает в стор
     * после установления INVITE. Без синхронного замка карусельный вызов,
     * прилетевший в это окно, был бы принят ПОВЕРХ создающегося исходящего —
     * два звонка на одном пульте (ревью 31.08.2026).
     */
    #placing = false;
    constructor(deps) {
        this.#deps = deps;
        this.#scheduler = deps.scheduler ?? systemScheduler;
        this.#audio =
            deps.audio ??
                new AudioDeviceManager({
                    ...browserAudioDeps(),
                    onSelectionChange: (selection) => {
                        this.#store.setDevices(selection.inputId, selection.outputId);
                    },
                });
    }
    getState() {
        return this.#store.getSnapshot();
    }
    subscribe(listener) {
        return this.#store.subscribe(listener);
    }
    setLeaderTab(isLeader) {
        this.#store.setLeaderTab(isLeader);
    }
    async connect(config) {
        if (this.#stack)
            return;
        this.#store.setLine('connecting');
        this.#store.setError(null);
        const stack = this.#deps.createStack(config);
        this.#stack = stack;
        if (this.#deps.audioElement)
            this.#audio.attachOutputElement(this.#deps.audioElement);
        this.#audio.start();
        this.#transport = new TransportSupervisor(stack.transport, {
            scheduler: this.#scheduler,
            isCallActive: () => this.#isCallActive(),
            onState: (state) => {
                if (state === 'connected')
                    this.#registerer?.refreshNow();
                else if (state === 'connecting')
                    this.#store.setLine('connecting');
            },
            onError: (error) => {
                this.#store.setError(describeError(error));
            },
        });
        this.#registerer = new RegistererSupervisor(stack.registerer, {
            scheduler: this.#scheduler,
            ...(config.registerExpiresSec !== undefined ? { expiresSec: config.registerExpiresSec } : {}),
            onLineUp: () => {
                this.#store.setLine('registered');
                this.#store.setError(null);
            },
            // Раздача на диспетчера без регистрации прекращается на сервере
            // (dispatcherCapacity → blockedReason='no_sip'), здесь только индикация.
            onLineDown: () => {
                this.#store.setLine('line_down');
            },
            onError: (error) => {
                this.#store.setError(describeError(error));
            },
        });
        // Карусель (§7.11): станция сама звонит пульту с уже снятым абонентом
        // на линии — такой вызов принимается без участия человека
        this.#unbindIncoming = stack.onIncomingCall((incoming) => {
            void this.#acceptCarouselCall(incoming);
        });
        this.#transport.start();
        this.#registerer.start();
        await this.#audio.prime();
        // Доступ выдан — только теперь у устройств есть названия и сохранённый
        // выбор можно вернуть. Без этого шага диспетчер каждое утро видит
        // «системное по умолчанию», хотя вчера выбрал гарнитуру
        await this.#audio.restore();
    }
    async disconnect() {
        this.#unbindIncoming?.();
        this.#unbindIncoming = null;
        this.#watchdog?.detach();
        this.#watchdog = null;
        await this.#call?.hangup('hangup_local').catch(() => {
            // Станция могла уже закрыть сессию — на выходе это не ошибка
        });
        this.#call = null;
        this.#registerer?.stop();
        this.#registerer = null;
        this.#transport?.stop();
        this.#transport = null;
        this.#audio.stop();
        await this.#stack?.dispose();
        this.#stack = null;
        this.#store.setLine('offline');
    }
    async call(request) {
        const stack = this.#stack;
        if (!stack)
            throw new Error('Софтфон не подключён к станции');
        if (!this.#store.getSnapshot().isLeaderTab) {
            throw new Error('Пульт открыт в другой вкладке — звонок отсюда невозможен');
        }
        if (this.#placing || this.#isCallActive())
            throw new Error('Звонок уже идёт');
        // Замок ставится синхронно, до первого await: карусельный вызов в окне
        // установления обязан увидеть занятость и отклониться
        this.#placing = true;
        try {
            const watchdog = this.#newWatchdog();
            this.#watchdog = watchdog;
            // Выбранный микрофон мог пропасть без события devicechange: набор с
            // несуществующим устройством падал «Requested device not found»
            // (15.09.2026, Таня). Сначала сверяемся со списком устройств…
            const inputId = await this.#audio.ensureInputAvailable();
            const place = (deviceId) => OutboundCall.place(stack, request, {
                scheduler: this.#scheduler,
                ...(deviceId !== null ? { inputDeviceId: deviceId } : {}),
                hooks: this.#sessionHooks(watchdog),
            });
            let call;
            try {
                call = await place(inputId);
            }
            catch (error) {
                // …а если браузер всё равно не нашёл устройство — один повтор с
                // системным микрофоном. Остальные ошибки микрофона — по-русски
                if (inputId === null || !isMissingDeviceError(error))
                    throw asMediaError(error);
                this.#audio.resetInput();
                try {
                    call = await place(null);
                }
                catch (retryError) {
                    throw asMediaError(retryError);
                }
            }
            this.#call = call;
            this.#store.startCall({
                id: call.id,
                assignmentId: request.assignmentId,
                phone: request.phone,
                startedAt: this.#scheduler.now(),
            });
            return {
                id: call.id,
                assignmentId: call.assignmentId,
                phone: call.phone,
                hangup: () => call.hangup('hangup_local'),
            };
        }
        finally {
            this.#placing = false;
        }
    }
    async hangup() {
        await this.#call?.hangup('hangup_local');
    }
    setMuted(value) {
        this.#call?.setMuted(value);
        this.#store.setMuted(value);
    }
    async setInputDevice(deviceId) {
        const track = await this.#audio.setInput(deviceId);
        // В разговоре дорожка подменяется без re-INVITE, иначе будет пауза в звуке
        if (track && this.#call)
            await this.#call.replaceInputTrack(track);
    }
    async setOutputDevice(deviceId) {
        await this.#audio.setOutput(deviceId);
    }
    clearCall() {
        this.#store.clearCall();
    }
    #isCallActive() {
        const call = this.#store.getSnapshot().call;
        return call !== null && call.state !== 'ended';
    }
    /** ICE-сторож звонка: два restart, потом звонок признаётся потерянным (§7.9) */
    #newWatchdog() {
        return new IceWatchdog({
            scheduler: this.#scheduler,
            graceMs: ICE_GRACE_MS,
            maxRestarts: ICE_MAX_RESTARTS,
            onRestart: async () => {
                await this.#call?.restartIce();
            },
            onGiveUp: () => {
                void this.#call?.hangup('network_error');
            },
        });
    }
    /** Общие хуки сессии: исходящий звонок и принятый карусельный ведутся одинаково */
    #sessionHooks(watchdog) {
        return {
            onStateChange: (state, at) => {
                if (state !== 'ended')
                    this.#store.advanceCall(state, at);
            },
            onEnded: (reason) => {
                this.#store.endCall(reason);
                watchdog.detach();
                this.#call = null;
            },
            onPeerConnection: (pc) => {
                watchdog.attach(pc);
            },
            onRemoteStream: (stream) => {
                const element = this.#deps.audioElement;
                if (!element)
                    return;
                element.srcObject = stream;
                void element.play().catch(() => {
                    // Автовоспроизведение может быть запрещено до первого клика —
                    // на пульте клик уже был (кнопка «Позвонить»), но не на всех платформах.
                });
            },
        };
    }
    /**
     * Входящий вызов от станции (карусель, §7.11).
     *
     * Принимается без участия человека, но только когда все три условия сошлись:
     * вызов помечен заголовком карусели, вкладка — лидер (иначе трубку снимут
     * две вкладки разом), активного звонка нет. Всё остальное — отклоняется:
     * станция увидит 486 и отдаст абонента другому свободному или в перезвон.
     */
    async #acceptCarouselCall(incoming) {
        const auto = incoming.getHeader(CAROUSEL_AUTO_HEADER);
        const leader = this.#store.getSnapshot().isLeaderTab;
        if (auto !== '1' || !leader || this.#placing || this.#isCallActive() || !soundUnlocked()) {
            await incoming.reject().catch(() => {
                // Станция уже сняла вызов — отклонять нечего
            });
            return;
        }
        // Тот же замок, что у call(): пока принимается этот вызов, ни исходящий,
        // ни второй карусельный не должны пролезть в окно установления
        this.#placing = true;
        const assignmentId = incoming.getHeader(ASSIGNMENT_HEADER) ?? '';
        const phone = incoming.getHeader(CAROUSEL_PHONE_HEADER) ?? '';
        // Та же сверка, что перед исходящим: принять звонок с пропавшим
        // микрофоном значит соединить абонента с тишиной
        const inputId = await this.#audio.ensureInputAvailable();
        const watchdog = this.#newWatchdog();
        this.#watchdog = watchdog;
        try {
            const session = await incoming.accept({
                iceGatheringTimeoutMs: ICE_GATHERING_TIMEOUT_MS,
                ...(inputId !== null ? { inputDeviceId: inputId } : {}),
            });
            const call = OutboundCall.fromSession(session, { phone, assignmentId, traceId: incoming.id }, { scheduler: this.#scheduler, hooks: this.#sessionHooks(watchdog) });
            this.#call = call;
            const now = this.#scheduler.now();
            this.#store.startCall({ id: call.id, assignmentId, phone, startedAt: now, isCarousel: true });
            // Абонент уже на линии: соединение состоялось в момент accept
            this.#store.advanceCall('in_call', now);
            const pc = session.getPeerConnection();
            if (pc)
                watchdog.attach(pc);
            const stream = session.getRemoteStream();
            if (stream && this.#deps.audioElement) {
                this.#deps.audioElement.srcObject = stream;
                void this.#deps.audioElement.play().catch(() => {
                    // Автовоспроизведение без клика может быть запрещено платформой
                });
            }
        }
        catch (error) {
            watchdog.detach();
            this.#watchdog = null;
            this.#store.setError(describeError(error));
        }
        finally {
            this.#placing = false;
        }
    }
}
export function createSoftphoneClient(deps) {
    return new Softphone(deps);
}
/**
 * Был ли на странице хоть один жест пользователя (§7.11, инцидент 01.09.2026).
 *
 * Без жеста браузер не даст играть звук, и авто-снятый карусельный звонок
 * был бы НЕМЫМ: диспетчер не слышит абонента, абонент слушает тишину.
 * Честнее отклонить — станция отдаст абонента другому свободному или в
 * перезвон. Среда без API (node в тестах, старые браузеры) считается
 * разблокированной: там и ограничения нет.
 */
function soundUnlocked() {
    if (typeof navigator === 'undefined')
        return true;
    const activation = navigator.userActivation;
    return activation === undefined ? true : activation.hasBeenActive;
}
function describeError(error) {
    return error instanceof Error ? error.message : String(error);
}
/**
 * Устройства и хранилище выбора для боевого запуска в браузере.
 *
 * Ядро прогоняется тестами в node, где ни navigator.mediaDevices, ни localStorage
 * нет (а глобальный navigator в node 22 есть, но без mediaDevices) — поэтому
 * проверяем наличие каждого API по отдельности, а не «мы в браузере».
 * Без этого AudioDeviceManager внутри клиента остаётся пустым: выбор микрофона
 * из пульта ничего не меняет, а setSinkId не применяется.
 */
function browserAudioDeps() {
    const media = typeof navigator !== 'undefined' && navigator.mediaDevices
        ? navigator.mediaDevices
        : undefined;
    const storage = typeof localStorage !== 'undefined' ? localStorage : undefined;
    return {
        ...(media !== undefined ? { mediaDevices: media } : {}),
        ...(storage !== undefined ? { storage } : {}),
    };
}
/* ─────────────────────── боевой адаптер SIP.js ───────────────────────
 *
 * Ниже — единственное место в пакете, которое знает про SIP.js. Всё остальное
 * ядро работает через порты из ports.ts, поэтому переподключение, поддержание
 * регистрации, ICE-сторож и машина состояний прогоняются тестами в node.
 */
/** Состояние транспорта SIP.js → состояние нашего порта */
export function toTransportState(state) {
    switch (state) {
        case SipTransportState.Connecting:
            return 'connecting';
        case SipTransportState.Connected:
            return 'connected';
        // Disconnecting сводим к disconnected: звонить в этот момент уже нельзя.
        // Супервизор от этого не «дёргается»: повторное disconnected он игнорирует,
        // пока не отработал заведённый таймер переподключения.
        case SipTransportState.Disconnecting:
        case SipTransportState.Disconnected:
            return 'disconnected';
    }
}
/**
 * Состояние регистрации SIP.js → состояние нашего порта.
 *
 * Initial (REGISTER ещё не отправлен) и Unregistered для нас одно и то же:
 * звонить нельзя. Промежуточное 'registering' SIP.js наружу не отдаёт —
 * его выставляет адаптер сам, когда отправляет REGISTER.
 */
export function toRegistrationState(state) {
    switch (state) {
        case SipRegistererState.Registered:
            return 'registered';
        case SipRegistererState.Initial:
        case SipRegistererState.Unregistered:
            return 'unregistered';
        case SipRegistererState.Terminated:
            return 'terminated';
    }
}
/**
 * Состояние сессии SIP.js → состояние нашего порта.
 *
 * Terminating (BYE отправлен, ответа ещё нет) сводим к terminated: для пульта
 * звонок уже кончился. Ranks ниже не дадут показать «завершён» дважды.
 * 'ringing' здесь появиться не может — оно приходит не состоянием сессии,
 * а провизорным ответом 180/183 на INVITE.
 */
export function toSipSessionState(state) {
    switch (state) {
        case SessionState.Initial:
        case SessionState.Establishing:
            return 'establishing';
        case SessionState.Established:
            return 'established';
        case SessionState.Terminating:
        case SessionState.Terminated:
            return 'terminated';
    }
}
/**
 * Ранг состояния сессии: то же правило, что у FSM звонка на сервере (§7.4).
 * Опоздавший 180 Ringing после ответа абонента не должен откатывать звонок.
 */
const SIP_SESSION_RANK = {
    establishing: 1,
    ringing: 2,
    established: 3,
    terminated: 4,
};
/**
 * Что диалплан согласен принять как номер: `_[+*#0-9].` в extensions.conf.
 * Проверка тут не косметическая: номер приходит из карточки, и без неё строка
 * вида `1@evil.example` увела бы INVITE на чужой сервер мимо станции.
 */
const DIALABLE_PHONE = /^[+*#0-9]{1,32}$/;
/** SIP URI вызываемого: номер + домен станции, взятый из sipUri диспетчера */
export function buildTargetUri(realm, phone) {
    if (!DIALABLE_PHONE.test(phone)) {
        throw new Error(`Номер «${phone}» не годится для набора: разрешены только + * # и цифры`);
    }
    return `sip:${phone}@${realm}`;
}
/** Ограничения на локальный поток: выбранный диспетчером микрофон, видео не нужно */
function micConstraints(deviceId) {
    return {
        audio: deviceId !== undefined ? { deviceId: { exact: deviceId } } : true,
        video: false,
    };
}
/**
 * Параметры Inviter для исходящего INVITE.
 *
 * Вынесено отдельной функцией не ради красоты: это единственный шов, на котором
 * можно проверить в node, что X-Assignment-Id и короткий таймаут сбора кандидатов
 * действительно доезжают до SIP.js. Живого сокета для этого не требуется.
 */
export function buildInviterOptions(options) {
    const handlerOptions = {
        constraints: micConstraints(options.inputDeviceId),
        // Полный сбор кандидатов — 3-10 секунд на каждый набор при пустой выгоде
        iceGatheringTimeout: options.iceGatheringTimeoutMs,
    };
    return {
        extraHeaders: options.extraHeaders,
        sessionDescriptionHandlerOptions: handlerOptions,
    };
}
/** Web-реализация SDH — только у неё есть peerConnection и remoteMediaStream */
function webHandler(session) {
    const handler = session.sessionDescriptionHandler;
    return handler instanceof Web.SessionDescriptionHandler ? handler : null;
}
/**
 * Транспорт SIP.js под наш порт.
 *
 * Первое подключение — start(), последующие — reconnect(). Разница существенная:
 * start() при уже запущенном UserAgent пишет предупреждение в лог и молча
 * резолвится, сокет при этом не открывается. Супервизор счёл бы линию поднятой
 * при мёртвом сокете — ровно тот отказ, ради которого свой супервизор и заведён.
 */
class SipJsTransport {
    #userAgent;
    #started = false;
    constructor(userAgent) {
        this.#userAgent = userAgent;
    }
    getState() {
        return toTransportState(this.#userAgent.transport.state);
    }
    async connect() {
        if (this.#started) {
            await this.#userAgent.reconnect();
            return;
        }
        // Флаг ставится до вызова: start() переводит UserAgent в Started сразу,
        // ещё до того как сокет открылся, и повторный start() уже был бы пустышкой
        this.#started = true;
        await this.#userAgent.start();
    }
    async disconnect() {
        await this.#userAgent.transport.disconnect();
    }
    onStateChange(listener) {
        const bridge = (state) => {
            listener(toTransportState(state));
        };
        this.#userAgent.transport.stateChange.addListener(bridge);
        return () => {
            this.#userAgent.transport.stateChange.removeListener(bridge);
        };
    }
}
/**
 * Регистрация SIP.js под наш порт.
 *
 * Состояние собирается из трёх источников, и это не избыточность:
 * 1. ответ на наш REGISTER (onAccept/onReject) — самый точный сигнал;
 * 2. stateChange самого Registerer — ловит протухание регистрации по таймеру
 *    и Terminated при остановке UserAgent;
 * 3. потеря транспорта — сокета нет, значит регистрации нет, что бы ни думал
 *    Registerer. Без этого пункта после обрыва индикатор оставался бы зелёным,
 *    а звонки не проходили: самый неприятный отказ из §7.9.
 */
class SipJsRegisterer {
    #userAgent;
    #listeners = new Set();
    #registerer = null;
    #unbindRegisterer = null;
    #expiresSec;
    #state = 'unregistered';
    constructor(userAgent, expiresSec) {
        this.#userAgent = userAgent;
        this.#expiresSec = expiresSec;
    }
    getState() {
        return this.#state;
    }
    onStateChange(listener) {
        this.#listeners.add(listener);
        return () => {
            this.#listeners.delete(listener);
        };
    }
    async register(options) {
        // Сокета нет — REGISTER отправлять некуда, и попытка вернётся локальной
        // ошибкой транспорта. Переподключением занят TransportSupervisor, а как
        // только он поднимет сокет, клиент сам позовёт refreshNow().
        if (!this.#userAgent.isConnected()) {
            this.#setState('unregistered');
            return;
        }
        const registerer = this.#ensureRegisterer(options.expiresSec);
        // Обновление живой регистрации не должно мигать индикатором: 'registering'
        // показываем только когда линии и правда нет
        if (this.#state !== 'registered')
            this.#setState('registering');
        try {
            await registerer.register({
                requestDelegate: {
                    // Своё решение о состоянии не принимаем: SIP.js к этому моменту уже
                    // разобрал Contact в ответе и мог признать регистрацию несостоявшейся
                    onAccept: () => {
                        this.#setState(toRegistrationState(registerer.state));
                    },
                    onReject: () => {
                        this.#setState(toRegistrationState(registerer.state));
                    },
                },
            });
        }
        catch (error) {
            // Предыдущий REGISTER ещё в полёте — по RFC 3261 §10.2 второй слать нельзя.
            // Это не отказ линии: ответ на первый придёт и состояние выставит он.
            if (error instanceof RequestPendingError)
                return;
            this.#setState('unregistered');
            throw error;
        }
    }
    async unregister() {
        const registerer = this.#registerer;
        if (!registerer)
            return;
        await registerer.unregister();
    }
    /**
     * Сокет до станции пропал. Регистрация вместе с ним недействительна, а объект
     * Registerer об этом не узнает и останется в Registered — поэтому его выбрасываем:
     * после переподключения нужен свежий, иначе успешный re-REGISTER не даст
     * перехода состояния и линия навсегда останется «потерянной».
     */
    notifyTransportLost() {
        this.#dropRegisterer();
        this.#setState('unregistered');
    }
    dispose() {
        this.#dropRegisterer();
        this.#listeners.clear();
    }
    #ensureRegisterer(expiresSec) {
        const current = this.#registerer;
        // Terminated-регистратор бросает на register(); expires задаётся только
        // в конструкторе, поэтому его смена — тоже повод пересоздать
        if (current && current.state !== SipRegistererState.Terminated && this.#expiresSec === expiresSec) {
            return current;
        }
        this.#dropRegisterer();
        this.#expiresSec = expiresSec;
        const registerer = new Registerer(this.#userAgent, {
            expires: expiresSec,
            // Конфигурация Registerer уходит в консоль как есть — незачем
            logConfiguration: false,
        });
        const bridge = (state) => {
            this.#setState(toRegistrationState(state));
        };
        registerer.stateChange.addListener(bridge);
        this.#unbindRegisterer = () => {
            registerer.stateChange.removeListener(bridge);
        };
        this.#registerer = registerer;
        return registerer;
    }
    #dropRegisterer() {
        this.#unbindRegisterer?.();
        this.#unbindRegisterer = null;
        const registerer = this.#registerer;
        this.#registerer = null;
        if (!registerer)
            return;
        void registerer.dispose().catch(() => {
            // Станция уже недоступна — снять регистрацию нечем и не нужно
        });
    }
    #setState(state) {
        if (this.#state === state)
            return;
        this.#state = state;
        for (const listener of this.#listeners)
            listener(state);
    }
}
/**
 * Одна исходящая сессия SIP.js под наш порт.
 *
 * Заголовки X-Assignment-Id и X-Call-Trace-Id уезжают в INVITE (§7.1): по первому
 * телефония находит карточку, по второму склеиваются логи пульта, API и CDR.
 */
class SipJsSession {
    id;
    #inviter;
    #iceGatheringTimeoutMs;
    #listeners = new Set();
    #unbindInviter = null;
    #state = 'establishing';
    constructor(inviter, iceGatheringTimeoutMs) {
        this.#inviter = inviter;
        this.id = inviter.id;
        this.#iceGatheringTimeoutMs = iceGatheringTimeoutMs;
        const bridge = (state) => {
            this.#setState(toSipSessionState(state));
        };
        inviter.stateChange.addListener(bridge);
        this.#unbindInviter = () => {
            inviter.stateChange.removeListener(bridge);
        };
    }
    /** Отправка INVITE. Отдельным шагом, чтобы подписка на состояния встала раньше сигналинга */
    async start() {
        try {
            await this.#inviter.invite({
                requestDelegate: {
                    onProgress: (response) => {
                        const code = response.message.statusCode;
                        // 180 Ringing и 183 Session Progress — у абонента пошёл гудок.
                        // Состояние сессии в SIP.js при этом всё ещё Establishing
                        if (code === 180 || code === 183)
                            this.#setState('ringing');
                    },
                },
            });
        }
        catch (error) {
            this.#unbindInviter?.();
            this.#unbindInviter = null;
            throw error;
        }
    }
    getState() {
        return this.#state;
    }
    onStateChange(listener) {
        this.#listeners.add(listener);
        return () => {
            this.#listeners.delete(listener);
        };
    }
    getPeerConnection() {
        return webHandler(this.#inviter)?.peerConnection ?? null;
    }
    getRemoteStream() {
        return webHandler(this.#inviter)?.remoteMediaStream ?? null;
    }
    /** До ответа абонента звонок снимается CANCEL, после — BYE: это разные запросы */
    async hangup() {
        switch (this.#inviter.state) {
            case SessionState.Initial:
            case SessionState.Establishing:
                await this.#inviter.cancel();
                return;
            case SessionState.Established:
                await this.#inviter.bye();
                return;
            case SessionState.Terminating:
            case SessionState.Terminated:
                return;
        }
    }
    /** re-INVITE с iceRestart: соединение чинится без разрыва разговора (§7.9) */
    async restartIce() {
        if (this.#inviter.state !== SessionState.Established)
            return;
        // Тип объявляется явно: InviterInviteOptions ждёт базовый интерфейс без полей
        // WebRTC, а нам нужны именно они — offerOptions и таймаут сбора кандидатов
        const handlerOptions = {
            offerOptions: { iceRestart: true },
            iceGatheringTimeout: this.#iceGatheringTimeoutMs,
        };
        await this.#inviter.invite({ sessionDescriptionHandlerOptions: handlerOptions });
    }
    #setState(state) {
        if (SIP_SESSION_RANK[state] <= SIP_SESSION_RANK[this.#state])
            return;
        this.#state = state;
        if (state === 'terminated') {
            this.#unbindInviter?.();
            this.#unbindInviter = null;
        }
        for (const listener of this.#listeners)
            listener(state);
    }
}
/**
 * Входящая сессия SIP.js (карусель, §7.11). Подписка на состояния встаёт в
 * конструкторе — до accept: established, пришедший раньше подписки, потерял бы
 * переход in_call, и разговор на пульте выглядел бы «звонящим» до конца.
 */
class SipJsInboundSession {
    id;
    #invitation;
    #listeners = new Set();
    #unbind = null;
    #state = 'ringing';
    constructor(invitation) {
        this.#invitation = invitation;
        this.id = invitation.id;
        const bridge = (state) => {
            this.#setState(toSipSessionState(state));
        };
        invitation.stateChange.addListener(bridge);
        this.#unbind = () => {
            invitation.stateChange.removeListener(bridge);
        };
    }
    async accept(options) {
        const constraints = {
            audio: options.inputDeviceId === undefined
                ? true
                : { deviceId: { exact: options.inputDeviceId } },
            video: false,
        };
        // Тип объявляется явно, как в restartIce: базовый интерфейс SIP.js полей
        // WebRTC не знает, а нужны именно они
        const handlerOptions = {
            constraints,
            iceGatheringTimeout: options.iceGatheringTimeoutMs,
        };
        await this.#invitation.accept({ sessionDescriptionHandlerOptions: handlerOptions });
    }
    async rejectCall() {
        if (this.#invitation.state === SessionState.Initial) {
            await this.#invitation.reject({ statusCode: 486 });
        }
    }
    getState() {
        return this.#state;
    }
    onStateChange(listener) {
        this.#listeners.add(listener);
        return () => {
            this.#listeners.delete(listener);
        };
    }
    getPeerConnection() {
        return webHandler(this.#invitation)?.peerConnection ?? null;
    }
    getRemoteStream() {
        return webHandler(this.#invitation)?.remoteMediaStream ?? null;
    }
    async hangup() {
        switch (this.#invitation.state) {
            case SessionState.Initial:
            case SessionState.Establishing:
                await this.#invitation.reject({ statusCode: 486 });
                return;
            case SessionState.Established:
                await this.#invitation.bye();
                return;
            case SessionState.Terminating:
            case SessionState.Terminated:
                return;
        }
    }
    async restartIce() {
        if (this.#invitation.state !== SessionState.Established)
            return;
        const handlerOptions = {
            offerOptions: { iceRestart: true },
            iceGatheringTimeout: ICE_GATHERING_TIMEOUT_MS,
        };
        await this.#invitation.invite({ sessionDescriptionHandlerOptions: handlerOptions });
    }
    #setState(state) {
        if (SIP_SESSION_RANK[state] <= SIP_SESSION_RANK[this.#state])
            return;
        this.#state = state;
        if (state === 'terminated') {
            this.#unbind?.();
            this.#unbind = null;
        }
        for (const listener of this.#listeners)
            listener(state);
    }
}
/** Входящий вызов: заголовки для решения + accept/reject (§7.11) */
class SipJsIncomingCall {
    id;
    #invitation;
    #session;
    constructor(invitation) {
        this.#invitation = invitation;
        this.id = invitation.id;
        this.#session = new SipJsInboundSession(invitation);
    }
    getHeader(name) {
        return this.#invitation.request.getHeader(name) ?? null;
    }
    async accept(options) {
        await this.#session.accept(options);
        return this.#session;
    }
    async reject() {
        await this.#session.rejectCall();
    }
}
/** SIP-стек целиком: транспорт, регистрация, исходящие сессии */
class SipJsStack {
    transport;
    registerer;
    #userAgent;
    #realm;
    #unbindTransport;
    #incomingListeners = new Set();
    constructor(userAgent, realm, expiresSec) {
        this.#userAgent = userAgent;
        this.#realm = realm;
        this.transport = new SipJsTransport(userAgent);
        this.registerer = new SipJsRegisterer(userAgent, expiresSec);
        // Подписка заводится до того, как супервизоры повесят свои: тогда к моменту
        // их вызова состояние регистрации уже согласовано с состоянием сокета
        this.#unbindTransport = this.transport.onStateChange((state) => {
            if (state !== 'connected')
                this.registerer.notifyTransportLost();
        });
        // Входящие от станции (карусель, §7.11). Без подписчика вызов отклоняется
        // сразу: висящий INVITE дозвонился бы в никуда и съел таймаут набора
        userAgent.delegate = {
            ...userAgent.delegate,
            onInvite: (invitation) => {
                if (this.#incomingListeners.size === 0) {
                    void invitation.reject({ statusCode: 486 }).catch(() => {
                        // Станция уже отменила вызов — отклонять нечего
                    });
                    return;
                }
                const call = new SipJsIncomingCall(invitation);
                for (const listener of this.#incomingListeners)
                    listener(call);
            },
        };
    }
    async invite(target, options) {
        const uri = UserAgent.makeURI(buildTargetUri(this.#realm, target));
        if (!uri)
            throw new Error(`Не удалось собрать SIP URI для номера «${target}»`);
        const inviter = new Inviter(this.#userAgent, uri, buildInviterOptions(options));
        const session = new SipJsSession(inviter, options.iceGatheringTimeoutMs);
        await session.start();
        return session;
    }
    onIncomingCall(listener) {
        this.#incomingListeners.add(listener);
        return () => {
            this.#incomingListeners.delete(listener);
        };
    }
    async dispose() {
        this.#unbindTransport();
        this.registerer.dispose();
        await this.#userAgent.stop();
    }
}
/**
 * Боевой адаптер SIP.js под порт SipStackPort.
 *
 * Собирает UserAgent на адрес станции из конфигурации и авторизацию диспетчера.
 * Встроенный реконнект SIP.js выключен намеренно (reconnectionAttempts: 0):
 * он молча сдаётся после нескольких попыток и не различает «сокет упал» и
 * «сокет жив, регистрация протухла». Переподключением занимается
 * TransportSupervisor, регистрацией — RegistererSupervisor.
 */
export function createSipJsStack(config) {
    const uri = UserAgent.makeURI(config.sipUri);
    if (!uri)
        throw new Error(`Не удалось разобрать SIP URI диспетчера: ${config.sipUri}`);
    const userAgent = new UserAgent({
        uri,
        transportOptions: {
            server: config.wssUrl,
            // Трассировка печатает SIP-сообщения целиком, включая заголовок Authorization.
            // Включать только руками при разборе конкретного инцидента
            traceSip: false,
        },
        authorizationUsername: config.authUser,
        authorizationPassword: config.password,
        ...(config.displayName !== undefined ? { displayName: config.displayName } : {}),
        // Логирование конфигурации печатает в консоль браузера authorizationPassword
        logConfiguration: false,
        logLevel: 'warn',
        reconnectionAttempts: 0,
        sessionDescriptionHandlerFactoryOptions: {
            iceGatheringTimeout: ICE_GATHERING_TIMEOUT_MS,
            peerConnectionConfiguration: {
                ...Web.defaultPeerConnectionConfiguration(),
                // Свой coturn и STUN приходят с сервера: чужой публичный STUN
                // из умолчания SIP.js в проде не нужен
                iceServers: config.iceServers,
            },
        },
    });
    return new SipJsStack(userAgent, uri.host, config.registerExpiresSec ?? REGISTER_EXPIRES_SEC);
}
//# sourceMappingURL=client.js.map